import {useEffect, useMemo, useState} from 'react'
import Chart from 'react-apexcharts'
import {useTranslation} from 'react-i18next'

import AppSelect from '../../../shared/AppSelect'
import {getCropDefinition} from '../../../shared/utils/cropCatalog'
import {
  buildCopilotCampaignLabels,
  buildDetailedAmbientComparisonRows,
  buildVegetationIndexChartData,
  getCopilotAmbientProductivity,
  getCopilotCampaignIdentity,
  getCopilotCampaignSourceLabel,
} from './copilotModel'
import SpecificCopilotIntegratedMap from './SpecificCopilotIntegratedMap'
import {withPdfChartReadiness} from './pdfVisualReadiness'
import {getTerrainEmptyMessageKey} from './terrainAvailability'

const finiteNumberOrNull = (value) => {
  if (value === '' || value === null || value === undefined) {
    return null
  }

  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

const formatNumber = (value, locale, digits = 2) => {
  const number = finiteNumberOrNull(value)
  if (number === null) {
    return '-'
  }

  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(number)
}

const formatMetric = (value, unit, locale, digits = 2) => {
  const number = finiteNumberOrNull(value)
  return number === null ? '-' : `${formatNumber(number, locale, digits)} ${unit}`
}

const uniqueCampaigns = (integrated) => {
  const campaigns = []
  const seen = new Set()
  const candidates = [
    ...(Array.isArray(integrated?.campaigns) ? integrated.campaigns : []),
    ...(Array.isArray(integrated?.ambientRows)
      ? integrated.ambientRows.flatMap((row) =>
          Array.isArray(row?.campaigns) ? row.campaigns : []
        )
      : []),
  ]

  candidates.forEach((campaign) => {
    const key = String(campaign?.campaignKey || '').trim()
    if (!key || seen.has(key)) {
      return
    }
    seen.add(key)
    campaigns.push(campaign)
  })

  return campaigns
}

const CROP_TRANSLATION_KEYS = {
  corn: 'corn',
  maize: 'corn',
  maíz: 'corn',
  peanut: 'peanut',
  soybean: 'soybean',
  soy: 'soybean',
  wheat: 'wheat',
}

const formatCrop = (crop, t) => {
  const value = String(crop || '').trim()
  const translationKey = CROP_TRANSLATION_KEYS[value.toLowerCase()] || getCropDefinition(value)?.economicId
  return translationKey ? t(`integrated_analysis.crops.${translationKey}`) : value || '-'
}

const campaignLabel = (campaign, t, labels = {}) => {
  const identity = getCopilotCampaignIdentity(campaign)
  return (
    labels[identity] ||
    [campaign?.campaign, campaign?.crop ? formatCrop(campaign.crop, t) : null]
      .filter(Boolean)
      .join(' - ') ||
    '-'
  )
}

const SoilCell = ({row, locale, t}) => {
  const details = []
  const productivityIndex = finiteNumberOrNull(row?.dominantSoilProductivityIndex)
  const share = finiteNumberOrNull(row?.dominantSoilSharePercent)

  if (productivityIndex !== null) {
    details.push(
      t('integrated_analysis.soil_productivity_index', {
        value: formatNumber(productivityIndex, locale, 0),
      })
    )
  }
  if (share !== null) {
    details.push(
      t('integrated_analysis.soil_share', {
        value: formatNumber(share, locale, 1),
      })
    )
  }

  return (
    <span className='specific-copilot-integrated__cell-stack'>
      <strong>{row?.dominantSoilUnit || '-'}</strong>
      {details.length > 0 && <small>{details.join(' · ')}</small>}
    </span>
  )
}

const AmbientCell = ({descriptor}) => (
  <span className='specific-copilot-integrated__cell-stack'>
    <strong>{descriptor.code}</strong>
    {descriptor.name && <small>{descriptor.name}</small>}
  </span>
)

const SoilUnitCell = ({cell, locale, t}) => {
  const productivityIndex = finiteNumberOrNull(cell?.soilProductivityIndex)

  return (
    <span className='specific-copilot-integrated__cell-stack'>
      <strong>{cell?.soilUnit || '-'}</strong>
      {productivityIndex !== null && (
        <small>
          {t('integrated_analysis.soil_productivity_index', {
            value: formatNumber(productivityIndex, locale, 0),
          })}
        </small>
      )}
    </span>
  )
}

const EmptyTableRow = ({colSpan, children}) => (
  <tr>
    <td className='specific-copilot-integrated__empty' colSpan={colSpan}>
      {children}
    </td>
  </tr>
)

const SpecificCopilotIntegratedAnalysis = ({analysis, observational = false}) => {
  const {t, i18n} = useTranslation('copilot')
  const locale = i18n.language || 'es'
  const integrated = analysis?.integratedAnalysis
  const ambientRows = useMemo(
    () => (Array.isArray(integrated?.ambientRows) ? integrated.ambientRows : []),
    [integrated]
  )
  const campaigns = useMemo(() => uniqueCampaigns(integrated), [integrated])
  const campaignLabels = useMemo(
    () =>
      buildCopilotCampaignLabels(campaigns, {
        locale,
        formatCrop: (crop) => formatCrop(crop, t),
      }),
    [campaigns, locale, t]
  )
  const ambientValues = useMemo(() => ambientRows.map((row) => row?.ambientClass), [ambientRows])
  const ambientDescriptors = useMemo(
    () =>
      ambientRows
        .map((row) => ({
          row,
          ...getCopilotAmbientProductivity(row?.ambientClass, ambientValues, locale),
        }))
        .sort((left, right) => {
          if (left.rank !== null && right.rank !== null) {
            return right.rank - left.rank
          }
          return String(right.code).localeCompare(String(left.code), locale)
        }),
    [ambientRows, ambientValues, locale]
  )
  const campaignOptions = useMemo(
    () =>
      campaigns.map((campaign) => ({
        value: String(campaign.campaignKey),
        label: campaignLabel(campaign, t, campaignLabels),
      })),
    [campaignLabels, campaigns, t]
  )
  const ambientOptions = useMemo(
    () =>
      ambientDescriptors.map((descriptor) => ({
        value: String(descriptor.row?.ambientClass),
        label: descriptor.label,
      })),
    [ambientDescriptors]
  )
  const [selectedCampaignKey, setSelectedCampaignKey] = useState('')
  const [selectedAmbientClass, setSelectedAmbientClass] = useState('')

  useEffect(() => {
    setSelectedCampaignKey((current) =>
      campaignOptions.some((option) => option.value === current)
        ? current
        : campaignOptions[0]?.value || ''
    )
  }, [campaignOptions])

  useEffect(() => {
    setSelectedAmbientClass((current) =>
      ambientOptions.some((option) => option.value === current)
        ? current
        : ambientOptions[0]?.value || ''
    )
  }, [ambientOptions])

  if (integrated?.status !== 'available' || ambientRows.length === 0) {
    return null
  }

  const activeCampaignKey =
    campaignOptions.find((option) => option.value === selectedCampaignKey)?.value ||
    campaignOptions[0]?.value ||
    ''
  const activeAmbientClass =
    ambientOptions.find((option) => option.value === selectedAmbientClass)?.value ||
    ambientOptions[0]?.value ||
    ''
  const selectedAmbient = ambientDescriptors.find(
    (descriptor) => String(descriptor.row?.ambientClass) === activeAmbientClass
  )
  const selectedCampaign = campaigns.find(
    (campaign) => String(campaign?.campaignKey) === activeCampaignKey
  )
  const selectedTripleCampaign = (
    Array.isArray(integrated?.tripleCross?.campaigns) ? integrated.tripleCross.campaigns : []
  ).find((campaign) => String(campaign?.campaignKey) === activeCampaignKey)
  const chartData = buildVegetationIndexChartData(
    selectedAmbient?.row?.ndvi?.observations,
    selectedAmbient?.row?.evi?.observations
  )
  const vegetationValues = [...chartData.ndvi, ...chartData.evi].map((point) => point.y)
  const chartOptions = {
    chart: {
      animations: {enabled: false},
      fontFamily: 'Poppins, sans-serif',
      toolbar: {show: false},
      zoom: {enabled: false},
    },
    colors: ['#16a34a', '#0ea5e9'],
    dataLabels: {enabled: false},
    grid: {
      borderColor: '#e7eaf0',
      strokeDashArray: 3,
    },
    legend: {
      fontSize: '11px',
      fontWeight: 600,
      horizontalAlign: 'left',
      position: 'top',
    },
    markers: {size: 4, strokeWidth: 0},
    noData: {text: t(observational ? 'integrated_analysis.no_index_data_period' : 'integrated_analysis.no_index_data')},
    stroke: {curve: 'straight', lineCap: 'round', width: [2, 2]},
    tooltip: {
      shared: true,
      intersect: false,
      x: {
        formatter: (value) =>
          new Intl.DateTimeFormat(locale, {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          }).format(new Date(Number(value))),
      },
      y: {
        formatter: (value) => formatNumber(value, locale, 2),
      },
    },
    xaxis: {
      type: 'datetime',
      labels: {
        datetimeUTC: true,
        style: {colors: '#7c849b', fontSize: '10px'},
      },
    },
    yaxis: {
      min: vegetationValues.some((value) => value < 0) ? -1 : 0,
      max: 1,
      tickAmount: vegetationValues.some((value) => value < 0) ? 4 : 5,
      labels: {
        formatter: (value) => formatNumber(value, locale, 2),
        style: {colors: '#7c849b', fontSize: '10px'},
      },
    },
  }
  const selectedTerrain = selectedAmbient?.row?.terrain
  const terrainStats = [
    ['minimum', selectedTerrain?.minElevationM],
    ['mean', selectedTerrain?.meanElevationM],
    ['maximum', selectedTerrain?.maxElevationM],
    ['range', selectedTerrain?.elevationRangeM],
  ]
  const campaignHistory = Array.isArray(selectedAmbient?.row?.campaigns)
    ? selectedAmbient.row.campaigns
    : []
  const tripleCells = buildDetailedAmbientComparisonRows(
    selectedTripleCampaign?.cells,
    ambientValues,
    locale
  )
  return (
    <section
      className='specific-copilot-integrated'
      aria-labelledby='copilot-integrated-title'
      data-pdf-ambient={selectedAmbient?.label || ''}
      data-pdf-campaign={campaignLabel(selectedCampaign, t, campaignLabels)}
    >
      <div className='specific-copilot-section-heading'>
        <h4 id='copilot-integrated-title'>{t('integrated_analysis.title')}</h4>
        <p>{t('integrated_analysis.subtitle')}</p>
      </div>

      <div className='specific-copilot-integrated__selectors'>
        <AppSelect
          label={t('integrated_analysis.ambient_label')}
          placeholder={t('integrated_analysis.ambient_placeholder')}
          options={ambientOptions}
          value={activeAmbientClass}
          onChange={setSelectedAmbientClass}
          disabled={ambientOptions.length < 2}
        />
        <AppSelect
          label={t('integrated_analysis.campaign_label')}
          placeholder={t('integrated_analysis.campaign_placeholder')}
          options={campaignOptions}
          value={activeCampaignKey}
          onChange={setSelectedCampaignKey}
          disabled={campaignOptions.length < 2}
        />
      </div>

      <SpecificCopilotIntegratedMap
        mapData={integrated?.map}
        campaignKey={activeCampaignKey}
        campaignLabel={campaignLabel(selectedCampaign, t, campaignLabels)}
        selectedAmbientClass={activeAmbientClass}
        ambientValues={ambientValues}
      />

      <div className='specific-copilot-integrated__detail-grid'>
        <article
          className='specific-copilot-chart'
          data-pdf-visual-kind='chart'
          data-pdf-visual-id='integrated-vegetation-indices'
          data-pdf-visual-state={
            chartData.ndvi.length > 0 || chartData.evi.length > 0 ? 'pending' : 'unavailable'
          }
        >
          <h5>{t('integrated_analysis.vegetation_title')}</h5>
          {chartData.ndvi.length > 0 || chartData.evi.length > 0 ? (
            <Chart
              type='line'
              height={260}
              options={withPdfChartReadiness(chartOptions)}
              series={[
                {
                  name: t('integrated_analysis.series.ndvi'),
                  data: chartData.ndvi,
                },
                {
                  name: t('integrated_analysis.series.evi'),
                  data: chartData.evi,
                },
              ]}
            />
          ) : (
            <div className='specific-copilot-chart__empty'>
              {t(observational ? 'integrated_analysis.no_index_data_period' : 'integrated_analysis.no_index_data')}
            </div>
          )}
        </article>

        <article className='specific-copilot-integrated__terrain'>
          <h5>{t('integrated_analysis.terrain_title')}</h5>
          {selectedTerrain ? (
            <>
              <dl>
                {terrainStats.map(([key, value]) => (
                  <div key={key}>
                    <dt>{t(`integrated_analysis.terrain.${key}`)}</dt>
                    <dd>{formatMetric(value, 'm', locale, 1)}</dd>
                  </div>
                ))}
              </dl>
              <p>
                {t('integrated_analysis.terrain_resolution', {
                  xy: formatMetric(integrated?.terrainResolutionXYMeters, 'm', locale, 1),
                  z: formatMetric(integrated?.terrainResolutionZMeters, 'm', locale, 1),
                })}
              </p>
            </>
          ) : (
            <div className='specific-copilot-integrated__empty-block'>
              {t(`integrated_analysis.${getTerrainEmptyMessageKey(integrated)}`)}
            </div>
          )}
        </article>
      </div>

      <div className='specific-copilot-integrated__subheading'>
        <h5>{t('integrated_analysis.campaign_history_title')}</h5>
        <p>{t('integrated_analysis.campaign_history_notice')}</p>
      </div>
      <div className='specific-copilot-integrated__table-wrap'>
        <table className='specific-copilot-integrated__table'>
          <thead>
            <tr>
              <th scope='col'>{t('integrated_analysis.columns.campaign')}</th>
              <th scope='col'>{t('integrated_analysis.columns.crop')}</th>
              <th scope='col'>{t('integrated_analysis.columns.report')}</th>
              <th scope='col'>{t('integrated_analysis.columns.surface')}</th>
              <th scope='col'>{t('integrated_analysis.columns.covered_surface')}</th>
              <th scope='col'>{t('integrated_analysis.columns.elevation')}</th>
              <th scope='col'>{t('integrated_analysis.columns.yield')}</th>
              <th scope='col'>{t('integrated_analysis.columns.margin')}</th>
            </tr>
          </thead>
          <tbody>
            {campaignHistory.length > 0 ? (
              campaignHistory.map((campaign) => (
                <tr key={String(campaign?.campaignKey)}>
                  <th scope='row'>{campaign?.campaign || '-'}</th>
                  <td>{formatCrop(campaign?.crop, t)}</td>
                  <td>{getCopilotCampaignSourceLabel(campaign, locale) || '-'}</td>
                  <td>{formatMetric(selectedAmbient?.row?.surfaceHa, 'ha', locale, 2)}</td>
                  <td>{formatMetric(campaign?.coveredAreaHa, 'ha', locale, 2)}</td>
                  <td>
                    {formatMetric(selectedAmbient?.row?.terrain?.meanElevationM, 'm', locale, 1)}
                  </td>
                  <td>{formatMetric(campaign?.economicYieldTonsHa, 't/ha', locale, 2)}</td>
                  <td>{formatMetric(campaign?.grossMarginUsdHa, 'USD/ha', locale, 2)}</td>
                </tr>
              ))
            ) : (
              <EmptyTableRow colSpan={8}>{t('integrated_analysis.no_campaign_data')}</EmptyTableRow>
            )}
          </tbody>
        </table>
      </div>

      <div className='specific-copilot-integrated__subheading'>
        <h5>{t('integrated_analysis.comparison_title')}</h5>
        <p>
          {t('integrated_analysis.comparison_notice', {
            campaign: campaignLabel(selectedCampaign, t, campaignLabels),
          })}
        </p>
      </div>
      <div className='specific-copilot-integrated__table-wrap'>
        <table className='specific-copilot-integrated__table'>
          <thead>
            <tr>
              <th scope='col'>{t('integrated_analysis.columns.ambient')}</th>
              <th scope='col'>{t('integrated_analysis.columns.soil')}</th>
              <th scope='col'>{t('integrated_analysis.columns.surface')}</th>
              <th scope='col'>{t('integrated_analysis.columns.covered_surface')}</th>
              <th scope='col'>{t('integrated_analysis.columns.elevation')}</th>
              <th scope='col'>{t('integrated_analysis.columns.yield')}</th>
              <th scope='col'>{t('integrated_analysis.columns.margin')}</th>
            </tr>
          </thead>
          <tbody>
            {ambientDescriptors.map((descriptor) => {
              const campaign = (descriptor.row?.campaigns || []).find(
                (item) => String(item?.campaignKey) === activeCampaignKey
              )
              return (
                <tr key={String(descriptor.row?.ambientClass)}>
                  <th scope='row'>
                    <AmbientCell descriptor={descriptor} />
                  </th>
                  <td>
                    <SoilCell row={descriptor.row} locale={locale} t={t} />
                  </td>
                  <td>{formatMetric(descriptor.row?.surfaceHa, 'ha', locale, 2)}</td>
                  <td>{formatMetric(campaign?.coveredAreaHa, 'ha', locale, 2)}</td>
                  <td>{formatMetric(descriptor.row?.terrain?.meanElevationM, 'm', locale, 1)}</td>
                  <td>{formatMetric(campaign?.economicYieldTonsHa, 't/ha', locale, 2)}</td>
                  <td>{formatMetric(campaign?.grossMarginUsdHa, 'USD/ha', locale, 2)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className='specific-copilot-integrated__subheading'>
        <h5>{t('integrated_analysis.triple_title')}</h5>
        <p>
          {t('integrated_analysis.comparison_notice', {
            campaign: campaignLabel(selectedCampaign, t, campaignLabels),
          })}
        </p>
        <p>{t('integrated_analysis.triple_notice')}</p>
      </div>
      <div className='specific-copilot-integrated__table-wrap'>
        <table className='specific-copilot-integrated__table'>
          <thead>
            <tr>
              <th scope='col'>{t('integrated_analysis.columns.ambient')}</th>
              <th scope='col'>{t('integrated_analysis.columns.soil_unit')}</th>
              <th scope='col'>{t('integrated_analysis.columns.surface')}</th>
              <th scope='col'>{t('integrated_analysis.columns.covered_surface')}</th>
              <th scope='col'>{t('integrated_analysis.columns.elevation')}</th>
              <th scope='col'>{t('integrated_analysis.columns.yield')}</th>
              <th scope='col'>{t('integrated_analysis.columns.margin')}</th>
            </tr>
          </thead>
          <tbody>
            {tripleCells.length > 0 ? (
              tripleCells.map((cell, index) => (
                <tr key={`${cell?.soilUnit || 'soil'}-${cell?.ambientClass || 'ambient'}-${index}`}>
                  <th scope='row'>
                    <AmbientCell descriptor={cell.descriptor} />
                  </th>
                  <td>
                    <SoilUnitCell cell={cell} locale={locale} t={t} />
                  </td>
                  <td>{formatMetric(cell?.surfaceHa, 'ha', locale, 2)}</td>
                  <td>{formatMetric(cell?.coveredAreaHa, 'ha', locale, 2)}</td>
                  <td>{formatMetric(cell?.meanElevationM, 'm', locale, 1)}</td>
                  <td>{formatMetric(cell?.economicYieldTonsHa, 't/ha', locale, 2)}</td>
                  <td>{formatMetric(cell?.grossMarginUsdHa, 'USD/ha', locale, 2)}</td>
                </tr>
              ))
            ) : (
              <EmptyTableRow colSpan={7}>{t('integrated_analysis.no_triple_data')}</EmptyTableRow>
            )}
          </tbody>
        </table>
      </div>
    </section>
  )
}

export default SpecificCopilotIntegratedAnalysis
